import { Link } from 'react-router-dom';
import { liveUpdates } from '@/mocks/live';
import { categories } from '@/mocks/categories';
import { topics } from '@/mocks/topics';
import TopicCard from '@/components/feature/TopicCard';
import { formatHeat } from '@/utils/format';

const hotTopics = [...topics].sort((a, b) => b.heat - a.heat).slice(0, 6);
const latestTopics = [...topics].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 4);

export default function LiveFeed() {
  return (
    <section id="live-feed" className="border-b border-background-200 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-shell px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.65fr_1fr]">
          <div>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700">
                  <span className="flex h-2 w-2 items-center justify-center">
                    <span className="block h-2 w-2 rounded-full bg-primary-500 animate-live" />
                  </span>
                  实时刷新中
                </span>
                <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[28px]">
                  实时更新流
                </h2>
                <p className="mt-2 text-sm text-foreground-700">
                  按时间倒序记录每一条刚上新的动态，点进去可以看到完整的时间线。
                </p>
              </div>
              <Link
                to="/topics"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
              >
                查看全部
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>

            <ol className="mt-7 border-l border-dashed border-background-300 pl-5">
              {liveUpdates.map((item) => (
                <li key={item.id} className="relative pb-6 last:pb-0">
                  <span className="absolute -left-[26px] top-1 flex h-3 w-3 items-center justify-center rounded-full border-2 border-background-50 bg-accent-500" />
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 font-medium text-accent-800">{item.time}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-refresh-line" aria-hidden="true" />
                      新内容上架
                    </span>
                  </div>
                  <Link
                    to={`/topic/${item.topicId}`}
                    className="mt-1.5 block text-sm font-medium leading-relaxed text-foreground-900 transition-colors hover:text-primary-700"
                  >
                    {item.text}
                  </Link>
                </li>
              ))}
            </ol>

            <h3 className="mt-10 font-heading text-lg font-bold text-foreground-950">最新上架</h3>
            <div className="mt-4 flex flex-col gap-3">
              {latestTopics.map((topic) => (
                <TopicCard key={topic.id} topic={topic} variant="horizontal" />
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-32 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-fire-line" aria-hidden="true" />
                </span>
                热度飙升榜
              </h3>
              <ol className="mt-4 flex flex-col gap-3">
                {hotTopics.map((topic, i) => (
                  <li key={topic.id} className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
                        i < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <Link
                        to={`/topic/${topic.id}`}
                        className="line-clamp-2 text-sm leading-snug text-foreground-900 transition-colors hover:text-primary-700"
                      >
                        {topic.title}
                      </Link>
                      <span className="mt-1 flex items-center gap-1 text-[11px] text-primary-700">
                        <i className="ri-fire-line" aria-hidden="true" />
                        {formatHeat(topic.heat)}
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">栏目速览</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {categories.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/topics?category=${item.slug}`}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                  >
                    <i className={item.icon} aria-hidden="true" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-information-line text-accent-700" aria-hidden="true" />
                编辑部提示
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                榜单依据阅读与讨论综合计算，仅代表当前关注度。遇到存疑内容，建议先看话题页的时间线，再作判断。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}